import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Camera, Key } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ProfileDialog({ open, onOpenChange }) {
  const { user, refresh } = useAuth();
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", extension: "", position: "",
    signature_block: "", signature_png_base64: "", code: "",
    reply_to_email: "", language: "de", timezone: "Europe/Berlin",
    editor_font_family: "", editor_font_size: "",
  });
  const [pw, setPw] = useState({ old_password: "", new_password: "", new_password_repeat: "" });
  const fileRef = useRef(null);
  const sigFileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/users/me").then((r) => {
      setMe(r.data);
      setForm({
        first_name: r.data.first_name || "",
        last_name: r.data.last_name || "",
        phone: r.data.phone || "",
        extension: r.data.extension || "",
        position: r.data.position || "",
        signature_block: r.data.signature_block || "",
        signature_png_base64: r.data.signature_png_base64 || "",
        code: r.data.code || "",
        reply_to_email: r.data.reply_to_email || "",
        language: r.data.language || "de",
        timezone: r.data.timezone || "Europe/Berlin",
        editor_font_family: r.data.editor_font_family || "",
        editor_font_size: r.data.editor_font_size ? String(r.data.editor_font_size) : "",
      });
    }).catch(() => {});
  }, [open]);

  const saveProfile = async () => {
    try {
      const payload = {
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        phone: form.phone || null,
        extension: form.extension || null,
        position: form.position || null,
        code: form.code || null,
        signature_block: form.signature_block || null,
        signature_png_base64: form.signature_png_base64 || null,
        reply_to_email: form.reply_to_email || null,
        language: form.language || null,
        timezone: form.timezone || null,
        editor_font_family: form.editor_font_family || null,
        editor_font_size: form.editor_font_size ? Number(form.editor_font_size) : null,
      };
      await api.patch("/users/me", payload);
      toast.success("Profil gespeichert");
      await refresh?.();
      const { data } = await api.get("/users/me");
      setMe(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/users/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMe((m) => ({ ...m, avatar_url: r.data.avatar_url, avatar_updated_at: Date.now() }));
      await refresh?.();
      toast.success("Profilbild aktualisiert");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen");
    }
  };

  const uploadSignaturePng = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Nur Bild-Dateien (PNG/JPG) sind erlaubt");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Datei zu groß (max. 2 MB)");
      return;
    }
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      setForm((s) => ({ ...s, signature_png_base64: dataUrl }));
      toast.success("Signatur geladen — nicht vergessen zu speichern!");
    } catch (err) {
      toast.error("Konnte Signatur nicht laden");
    }
  };

  const clearSignaturePng = () => {
    setForm((s) => ({ ...s, signature_png_base64: "" }));
  };

  const changePw = async () => {
    if (pw.new_password !== pw.new_password_repeat) {
      toast.error("Die neuen Passwörter stimmen nicht überein");
      return;
    }
    if (pw.new_password.length < 6) {
      toast.error("Das neue Passwort muss mindestens 6 Zeichen lang sein");
      return;
    }
    try {
      await api.post("/auth/change-password", { old_password: pw.old_password, new_password: pw.new_password });
      toast.success("Passwort geändert");
      setPw({ old_password: "", new_password: "", new_password_repeat: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Ändern des Passworts");
    }
  };

  const initials = (form.first_name?.[0] || me?.name?.[0] || "?").toUpperCase() + (form.last_name?.[0] || "").toUpperCase();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle>Mein Profil</DialogTitle>
          <DialogDescription>Persönliche Stammdaten und Anmeldedaten. Die E-Mail-Adresse wird vom Administrator vergeben und kann hier nicht geändert werden.</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="profile">
          <TabsList>
            <TabsTrigger value="profile" data-testid="profile-tab-data">Stammdaten</TabsTrigger>
            <TabsTrigger value="signature" data-testid="profile-tab-signature">Unterschrift</TabsTrigger>
            <TabsTrigger value="preferences" data-testid="profile-tab-prefs">Kommunikation</TabsTrigger>
            <TabsTrigger value="security" data-testid="profile-tab-security">Sicherheit</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-4 pt-3">
            <div className="flex items-center gap-4">
              <div className="relative">
                {me?.avatar_url ? (
                  <img
                    src={me.avatar_url.startsWith("http")
                      ? `${me.avatar_url}${me.avatar_url.includes("?") ? "&" : "?"}t=${Date.now()}`
                      : `${process.env.REACT_APP_BACKEND_URL || ""}${me.avatar_url}?t=${me.avatar_updated_at || Date.now()}`}
                    alt="Profilbild"
                    className="h-20 w-20 rounded-full object-cover border border-stone-200"
                    data-testid="profile-avatar-img"
                  />
                ) : (
                  <div className="h-20 w-20 rounded-full bg-stone-200 flex items-center justify-center text-2xl font-semibold text-slate-600" data-testid="profile-avatar-fallback">{initials}</div>
                )}
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="absolute -bottom-1 -right-1 bg-slate-900 text-white rounded-full p-1.5 hover:bg-slate-700"
                  title="Profilbild ändern"
                  data-testid="profile-avatar-upload-btn"
                >
                  <Camera size={14} />
                </button>
                <input type="file" ref={fileRef} accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="profile-avatar-input" />
              </div>
              <div className="text-sm text-slate-600">
                <div className="font-medium text-slate-900">{me?.email}</div>
                <div className="text-xs">{me?.role}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Vorname">
                <Input value={form.first_name} onChange={(e) => setForm((s) => ({ ...s, first_name: e.target.value }))} data-testid="profile-first-name" />
              </Field>
              <Field label="Nachname">
                <Input value={form.last_name} onChange={(e) => setForm((s) => ({ ...s, last_name: e.target.value }))} data-testid="profile-last-name" />
              </Field>
              <Field label="Telefonnummer">
                <Input value={form.phone} onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))} data-testid="profile-phone" placeholder="+49 …" />
              </Field>
              <Field label="Durchwahl">
                <Input value={form.extension} onChange={(e) => setForm((s) => ({ ...s, extension: e.target.value }))} data-testid="profile-extension" />
              </Field>
              <Field label="Position / Rolle">
                <Input
                  value={form.position}
                  onChange={(e) => setForm((s) => ({ ...s, position: e.target.value }))}
                  placeholder="z. B. Geschäftsführung, Objektbetreuung, Buchhaltung"
                  data-testid="profile-position"
                />
              </Field>
            </div>

            <div>
              <Label className="text-xs text-slate-700">Signatur</Label>
              <p className="text-[11px] text-slate-500 mt-0.5 mb-1.5">
                Alle Unterschriften-Einstellungen (Text-Block, Kürzel, Digitale PNG-Signatur)
                findest du im Tab <strong>Unterschrift</strong>.
              </p>
            </div>

            {/* Phase 21β-4 — Editor-Voreinstellungen für neue Dokumente */}
            <div className="border-t border-slate-200 pt-3 mt-1">
              <Label className="text-xs text-slate-700">Editor-Voreinstellungen</Label>
              <p className="text-[11px] text-slate-500 mt-0.5 mb-2">
                Schriftart und -größe für <span className="font-medium">neu erstellte</span> Schreiben und Vorlagen.
                Bestehende Dokumente behalten ihre Formatierung.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Schriftart">
                  <Select value={form.editor_font_family || "_default"}
                          onValueChange={(v) => setForm((s) => ({ ...s, editor_font_family: v === "_default" ? "" : v }))}>
                    <SelectTrigger data-testid="profile-font-family">
                      <SelectValue placeholder="Standard" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_default">Standard (Helvetica)</SelectItem>
                      <SelectItem value="Helvetica">Helvetica</SelectItem>
                      <SelectItem value="Arial">Arial</SelectItem>
                      <SelectItem value="Calibri">Calibri</SelectItem>
                      <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                      <SelectItem value="Georgia">Georgia</SelectItem>
                      <SelectItem value="Inter">Inter</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Schriftgröße (pt)">
                  <Select value={form.editor_font_size || "_default"}
                          onValueChange={(v) => setForm((s) => ({ ...s, editor_font_size: v === "_default" ? "" : v }))}>
                    <SelectTrigger data-testid="profile-font-size">
                      <SelectValue placeholder="Standard (11)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_default">Standard (11)</SelectItem>
                      {["9", "10", "10.5", "11", "11.5", "12", "13", "14"].map((s) => (
                        <SelectItem key={s} value={s}>{s} pt</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={saveProfile} className="bg-slate-900 hover:bg-slate-800" data-testid="profile-save-btn">Speichern</Button>
            </DialogFooter>
          </TabsContent>

          {/* 2026-02 (feb) — Unterschrift-Tab (aus SettingsDialog verschoben) */}
          <TabsContent value="signature" className="space-y-4 pt-3">
            <div className="text-sm text-slate-600">
              Standardmäßig wird in Briefen/Abrechnungen folgender Block verwendet:
              <span className="block text-xs text-slate-500 mt-1">
                „Mit freundlichen Grüßen / Vor- und Nachname (Profil) / Position"
              </span>
            </div>

            <Field label="Mein Zeichen (Kürzel)">
              <div className="space-y-1">
                <Input
                  value={form.code}
                  onChange={(e) => setForm((s) => ({ ...s, code: e.target.value.toUpperCase() }))}
                  placeholder={((form.first_name?.[0] || "") + (form.last_name?.[0] || "")).toUpperCase() || "z. B. MR"}
                  maxLength={6}
                  data-testid="profile-user-code"
                  className="font-mono w-32 uppercase"
                />
                <div className="text-[11px] text-slate-500 italic">
                  Wird als „Mein Zeichen" oben rechts auf Briefen ausgewiesen. Leer = automatische Initialen.
                </div>
              </div>
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="border border-slate-200 rounded-md p-3 bg-slate-50/40">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Aus deinem Profil</div>
                <div className="space-y-1">
                  <div><span className="text-slate-500">Name:</span> <span className="font-medium">{`${form.first_name || ""} ${form.last_name || ""}`.trim() || <em className="text-slate-400">– fehlt –</em>}</span></div>
                  <div><span className="text-slate-500">Position:</span> <span className="font-medium">{form.position || <em className="text-slate-400">– fehlt –</em>}</span></div>
                  <div><span className="text-slate-500">Kürzel:</span> <span className="font-mono font-medium">{form.code || ((form.first_name?.[0] || "") + (form.last_name?.[0] || "")).toUpperCase() || "—"}</span></div>
                </div>
              </div>
              <div className="border border-slate-200 rounded-md p-3 bg-emerald-50/30">
                <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold mb-2">Vorschau (Text-Signatur)</div>
                <pre className="text-xs whitespace-pre-wrap font-sans text-slate-800" data-testid="profile-signature-preview">
{form.signature_block?.trim() || `Mit freundlichen Grüßen\n\n${`${form.first_name || ""} ${form.last_name || ""}`.trim() || "(Name)"}\n${form.position || "(Position)"}`}
                </pre>
              </div>
            </div>

            <Field label="Eigener Unterschriftsblock (optional)">
              <div className="space-y-1">
                <textarea
                  rows={4}
                  value={form.signature_block}
                  onChange={(e) => setForm((s) => ({ ...s, signature_block: e.target.value }))}
                  placeholder="Leer lassen, um die Standard-Vorlage oben zu nutzen."
                  data-testid="profile-signature-block"
                  className="w-full text-sm border border-slate-200 rounded-sm px-3 py-2 focus:outline-none focus:border-slate-400"
                />
                <div className="text-[11px] text-slate-500 italic">
                  Wenn du hier Text einträgst, überschreibt dieser die Standard-Vorlage – sonst werden automatisch dein Name + deine Position genutzt.
                </div>
              </div>
            </Field>

            {/* Digitale PNG-Signatur für amtliche Dokumente */}
            <div className="border-t border-slate-200 pt-3 mt-2">
              <Label className="text-xs text-slate-700 font-semibold">Digitale Unterschrift (PNG)</Label>
              <p className="text-[11px] text-slate-500 mt-0.5 mb-2">
                Wird auf amtliche Dokumente (Wohnungsgeberbestätigung, Vermieterbescheinigung, …)
                über die Unterschriftslinie gelegt. Empfehlung: transparenter PNG,
                ca. 300×80 px, max. 2 MB.
              </p>
              <div className="flex items-start gap-4">
                <div className="w-64 h-24 border-2 border-dashed border-slate-300 rounded-md flex items-center justify-center bg-slate-50 overflow-hidden">
                  {form.signature_png_base64 ? (
                    <img src={form.signature_png_base64} alt="Signatur" className="max-h-20 max-w-60 object-contain" data-testid="profile-signature-png-preview" />
                  ) : (
                    <span className="text-xs text-slate-400">Keine Signatur hochgeladen</span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <Button variant="outline" size="sm" onClick={() => sigFileRef.current?.click()} data-testid="profile-signature-png-upload">
                    <Camera size={14} className="mr-1" /> {form.signature_png_base64 ? "Ersetzen" : "Hochladen"}
                  </Button>
                  {form.signature_png_base64 && (
                    <Button variant="ghost" size="sm" onClick={clearSignaturePng} data-testid="profile-signature-png-clear" className="text-red-600 hover:text-red-800">
                      Entfernen
                    </Button>
                  )}
                  <input type="file" ref={sigFileRef} accept="image/png,image/jpeg" className="hidden" onChange={uploadSignaturePng} data-testid="profile-signature-png-input" />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={saveProfile} className="bg-slate-900 hover:bg-slate-800" data-testid="profile-signature-save-btn">Speichern</Button>
            </DialogFooter>
          </TabsContent>

          {/* 2026-02 (feb) — Kommunikations-Tab (Reply-To, Sprache, Zeitzone) */}
          <TabsContent value="preferences" className="space-y-4 pt-3">
            <p className="text-xs text-slate-500">
              Ausgehende E-Mails, Sprache und Zeitzone.
            </p>
            <Field label="Antwortadresse (Reply-To)">
              <Input
                type="email"
                value={form.reply_to_email}
                onChange={(e) => setForm((s) => ({ ...s, reply_to_email: e.target.value }))}
                placeholder="antwort@meinedomain.de"
                data-testid="profile-reply-to"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Wenn gesetzt, wird diese Adresse als Reply-To in ausgehende E-Mails eingetragen. Empfänger antworten dann direkt hierhin.
              </p>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Sprache">
                <Select value={form.language} onValueChange={(v) => setForm((s) => ({ ...s, language: v }))}>
                  <SelectTrigger data-testid="profile-language"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="de">Deutsch</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Zeitzone">
                <Select value={form.timezone} onValueChange={(v) => setForm((s) => ({ ...s, timezone: v }))}>
                  <SelectTrigger data-testid="profile-timezone"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Europe/Berlin">Europa/Berlin (MEZ)</SelectItem>
                    <SelectItem value="Europe/Vienna">Europa/Wien</SelectItem>
                    <SelectItem value="Europe/Zurich">Europa/Zürich</SelectItem>
                    <SelectItem value="Europe/London">Europa/London (GMT)</SelectItem>
                    <SelectItem value="UTC">UTC</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <DialogFooter>
              <Button onClick={saveProfile} className="bg-slate-900 hover:bg-slate-800" data-testid="profile-prefs-save-btn">Speichern</Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="security" className="space-y-3 pt-3">
            <div className="text-xs text-slate-600">
              <Key size={14} className="inline mr-1" /> Aktuelles Passwort ändern.
            </div>
            <Field label="Aktuelles Passwort">
              <Input type="password" value={pw.old_password} onChange={(e) => setPw((s) => ({ ...s, old_password: e.target.value }))} data-testid="profile-old-password" />
            </Field>
            <Field label="Neues Passwort">
              <Input type="password" value={pw.new_password} onChange={(e) => setPw((s) => ({ ...s, new_password: e.target.value }))} data-testid="profile-new-password" />
            </Field>
            <Field label="Neues Passwort wiederholen">
              <Input type="password" value={pw.new_password_repeat} onChange={(e) => setPw((s) => ({ ...s, new_password_repeat: e.target.value }))} data-testid="profile-new-password-repeat" />
            </Field>
            <DialogFooter>
              <Button onClick={changePw} className="bg-slate-900 hover:bg-slate-800" data-testid="profile-change-password-btn">Passwort ändern</Button>
            </DialogFooter>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-slate-700">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/**
 * Phase 21β-3.4 — Composes a sensible default signature block from the user's
 * stammdaten. Mirrors the backend logic in macros.py so the live preview in
 * the profile dialog matches what the PDF generator will produce.
 */
function buildDefaultSignature({ first_name, last_name, position }) {
  const name = `${first_name || ""} ${last_name || ""}`.trim();
  const role = (position || "").trim();
  if (!name && !role) return "Mit freundlichen Grüßen\n\n[Name]\n[Position]";
  const lines = ["Mit freundlichen Grüßen", ""];
  if (name) lines.push(name);
  if (role) lines.push(role);
  return lines.join("\n");
}

/**
 * Stilisierte Anzeige des Signaturblocks — Name fett, Position klein/grau.
 * Spiegelt exakt das PDF-Rendering aus letterhead.py.
 */
function SignaturePreview({ block, authorName, authorRole }) {
  const lines = (block || "").split("\n");
  return (
    <div className="space-y-0.5 leading-snug text-slate-800" data-testid="profile-signature-preview">
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={i} className="h-2" />;
        if (authorName && trimmed === authorName) {
          return <div key={i} className="font-semibold text-slate-900">{trimmed}</div>;
        }
        if (authorRole && trimmed === authorRole) {
          return <div key={i} className="text-[11px] text-slate-500">{trimmed}</div>;
        }
        return <div key={i}>{trimmed}</div>;
      })}
    </div>
  );
}
